import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShuffleOutlinedIcon from "@mui/icons-material/ShuffleOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ColorField from "../admin/ColorField";
import IconField from "../admin/config/IconField";
import { STYLING_MANAGED_MESSAGE } from "../../utilities/stylingPermission";
import JsonField, { JsonValidityProvider, useJsonValidity } from "../admin/config/JsonField";
import {
  DEFAULT_ICON_SIZE,
  MAX_ICON_ZOOM,
  bandsToIconSize,
  getIconSize,
  iconSizeToBands,
  mergeCategoryChoices,
  type CmIconSize,
  type CmIconSizeBand,
  type CmStyleChoice
} from "../../utilities/cmTableStyle";
import {
  buildUpdatedRasterStyle,
  buildUpdatedSpatialVectorStyle,
  getRasterTier1Values,
  getSpatialVectorStyleChoices,
  getSpatialVectorStyleDefaultOutput,
  getSpatialVectorStyleFieldKey,
  isSpatialVectorIconStyled,
  spatialFieldValues,
  ENUMERATE_MAX,
  toCmSpatialType,
  type SpatialRasterTier1Values,
  type SpatialVectorGeometryType
} from "../../utilities/spatialDatasetStyle";
import { categoryColor } from "../../utilities/categoryPalette";
import type { SpatialDataset } from "../../types";

export interface SpatialDatasetStyleDialogProps {
  dataset: SpatialDataset | null;
  open: boolean;
  onClose: () => void;
  onSave: (displayStyle: Record<string, any>) => void;
  saving?: boolean;
  /** The dataset's own organisation, so the icon picker uploads/lists against the right tenant. */
  orgId?: number | null;
  /**
   * Without `manage_styling`: the style is shown but nothing can be changed, and
   * there is nothing to save — `display_style` is all this dialog writes.
   */
  readOnly?: boolean;
}

/** Only a 6-digit hex is safe to write into a style; anything else is ignored. */
const asHex = (value?: string): string | undefined => (value && /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim() : undefined);

/** A blank category row, typed for the geometry being edited. */
const emptyChoice = (geometryType: SpatialVectorGeometryType, primaryColor?: string): CmStyleChoice =>
  isSpatialVectorIconStyled(geometryType) ? { value: "", icon: "" } : { value: "", color: asHex(primaryColor) ?? "#3b82f6" };

/**
 * Reads the default rule's output into a choice-shaped value the controls can edit.
 *
 * A dataset with no default yet starts on the app's primary colour, which on this
 * surface *is* the organisation's brand colour — the MUI theme is built from
 * `config.colorScheme` (`themes/theme.tsx`). An existing default is never replaced.
 */
const defaultChoiceFrom = (
  style: Record<string, any> | undefined,
  geometryType: SpatialVectorGeometryType,
  primaryColor?: string
): CmStyleChoice => {
  const output = getSpatialVectorStyleDefaultOutput(style, geometryType) ?? "";
  if (isSpatialVectorIconStyled(geometryType)) return { value: "", icon: output };
  return { value: "", color: output || asHex(primaryColor) || "#9ca3af" };
};

/**
 * Styles a spatial dataset (vector PMTiles or raster COG/PMTiles) via its
 * `display_style`. Mirrors `TableStyleDialog.tsx`'s shape deliberately — same
 * category-editor pattern for vectors, same GeoStyler-JSON escape hatch — but
 * the field a vector rule filters on is a real attribute column here (there is
 * no CM-table-style `style_value` indirection to get wrong), and rasters get a
 * dedicated Tier-1 appearance section instead.
 *
 * Raster Tier 2 (`colorMap` value→colour recolouring) is NOT built here: it's
 * blocked on the worker writing raw-value COGs for single-band input (the 9 Sep
 * 2026 spatial-dataset styling plan, §4.2, decision D-6) — today's single-band
 * COGs are a baked 0–255 grey stretch, so a colour ramp would have nothing real
 * to map. The section below explains that rather than hiding it.
 */
const SpatialDatasetStyleDialog: React.FC<SpatialDatasetStyleDialogProps> = ({
  dataset,
  open,
  onClose,
  onSave,
  saving,
  orgId,
  readOnly = false
}) => (
  // Own validity scope so a malformed JSON here can't leak out to other dialogs.
  <JsonValidityProvider>
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      {dataset && (
        <DialogBody
          key={dataset.id}
          dataset={dataset}
          onClose={onClose}
          onSave={onSave}
          saving={saving}
          orgId={orgId}
          readOnly={readOnly}
        />
      )}
    </Dialog>
  </JsonValidityProvider>
);

const OPACITY_MARKS = [0, 0.5, 1].map((v) => ({ value: v, label: String(v) }));

/**
 * Most categories the dialog will prefill without being asked. Matches the worker's
 * enumeration ceiling, so a complete list is filled in full and only an
 * unenumerable column is held back — which the identifier guard catches anyway.
 */
const AUTO_FILL_MAX = ENUMERATE_MAX;

const DialogBody: React.FC<{
  dataset: SpatialDataset;
  onClose: () => void;
  onSave: (displayStyle: Record<string, any>) => void;
  saving?: boolean;
  orgId?: number | null;
  readOnly: boolean;
}> = ({ dataset, onClose, onSave, saving, orgId, readOnly }) => {
  const jsonInvalid = useJsonValidity();
  const primaryColor = useTheme().palette.primary.main;
  const isRaster = dataset.dataset_type === "raster";
  const geometryType: SpatialVectorGeometryType = dataset.geometry_type ?? null;
  const geometry = toCmSpatialType(geometryType);
  const iconStyled = isSpatialVectorIconStyled(geometryType);

  const [displayStyle, setDisplayStyle] = useState<Record<string, any>>(dataset.display_style ?? {});

  // ── Vector state (meaningful only when !isRaster) ─────────────────────────
  const attributeOptions = useMemo(() => (dataset.attribute_schema ?? []).map((f) => f.name), [dataset.attribute_schema]);
  const [fieldKey, setFieldKey] = useState<string>(
    () => getSpatialVectorStyleFieldKey(displayStyle) ?? dataset.attribute_schema?.[0]?.name ?? ""
  );
  const [choices, setChoices] = useState<CmStyleChoice[]>(() => getSpatialVectorStyleChoices(displayStyle, geometryType));
  const [fallback, setFallback] = useState<CmStyleChoice>(() => defaultChoiceFrom(displayStyle, geometryType, primaryColor));
  // Undefined means the stored size is an expression the form cannot model — leave it to the JSON tab.
  const [iconSize, setIconSize] = useState<CmIconSize | undefined>(() =>
    iconStyled ? (getIconSize(displayStyle) ?? DEFAULT_ICON_SIZE) : undefined
  );

  // Every value the dataset records for the chosen field — see `spatialFieldValues`
  // for why this can be a truncated sample and how that is surfaced.
  const fieldValues = useMemo(() => spatialFieldValues(dataset.attribute_schema, fieldKey), [dataset.attribute_schema, fieldKey]);
  const sampleValuesForField = fieldValues.values;

  // A stored style can name a column this dataset no longer reports (renamed, or
  // re-processed since). Keeping it in the list is what lets the field show what
  // the style is actually filtering on instead of reading as nothing selected.
  const fieldOptions = useMemo(
    () => (fieldKey && !attributeOptions.includes(fieldKey) ? [fieldKey, ...attributeOptions] : attributeOptions),
    [attributeOptions, fieldKey]
  );

  /** Columns the worker judged too high-cardinality to be styling categories. */
  const identifierFields = useMemo(
    () => new Set((dataset.attribute_schema ?? []).filter((f) => (f as any).high_cardinality).map((f) => f.name)),
    [dataset.attribute_schema]
  );

  const fieldHelperText = fieldValues.highCardinality
    ? "Nearly every feature has its own value here — this reads as an identifier, not a category."
    : fieldValues.complete
      ? `${fieldValues.distinctCount ?? fieldValues.values.length} distinct value${
          (fieldValues.distinctCount ?? fieldValues.values.length) === 1 ? "" : "s"
        } in this column.`
      : "Choosing a field styles every value it holds.";

  const styledValues = useMemo(() => new Set(choices.map((c) => c.value?.trim()).filter(Boolean)), [choices]);
  const unstyledCount = fieldValues.values.filter((value) => !styledValues.has(value)).length;

  /**
   * The values one row may pick: everything this column holds, less what the
   * OTHER rows already style.
   *
   * A category belongs to exactly one rule. Two rows naming one value make a
   * style that cannot render: a spatial dataset's rules compile into a single
   * `match`-driven layer, and MapLibre rejects a `match` that repeats a branch
   * label — which takes the whole dataset off the map rather than just that rule.
   * A list that never offers a value twice is the one place that can be prevented
   * outright rather than reported after the fact.
   *
   * The row's own value is always in its list: as the selected option (so the
   * field can show what it is set to), and prepended when the column's value list
   * does not contain it — a truncated sample, or a value stored before the list
   * was a closed set. Without that it would read as blank and be lost on save.
   */
  const optionsForChoice = (choice: CmStyleChoice): string[] => {
    const own = choice.value?.trim();
    const available = sampleValuesForField.filter((value) => value === own || !styledValues.has(value));
    return own && !available.includes(own) ? [own, ...available] : available;
  };

  const writeVectorStyle = useCallback(
    (nextChoices: CmStyleChoice[], nextFallback: CmStyleChoice, nextFieldKey: string, nextIconSize: CmIconSize | undefined) => {
      setChoices(nextChoices);
      setFallback(nextFallback);
      setFieldKey(nextFieldKey);
      setIconSize(nextIconSize);
      setDisplayStyle((prev) =>
        buildUpdatedSpatialVectorStyle(prev, geometryType, nextFieldKey || "value", nextChoices, nextFallback, nextIconSize)
      );
    },
    [geometryType]
  );

  const bands = iconSizeToBands(iconSize ?? DEFAULT_ICON_SIZE);
  const writeBands = (next: CmIconSizeBand[]) => writeVectorStyle(choices, fallback, fieldKey, bandsToIconSize(next));
  const setBandSize = (index: number, size: number) => writeBands(bands.map((b, i) => (i === index ? { ...b, size } : b)));
  const setBandEnd = (index: number, endZoom: number) => {
    const lower = bands[index].startZoom + 1;
    const upper = (bands[index + 2]?.startZoom ?? MAX_ICON_ZOOM) - 1;
    const clamped = Math.max(lower, Math.min(upper, Math.round(endZoom)));
    writeBands(bands.map((b, i) => (i === index ? { ...b, endZoom: clamped } : i === index + 1 ? { ...b, startZoom: clamped } : b)));
  };
  const addBand = () => {
    const last = bands[bands.length - 1];
    const boundary = Math.min(MAX_ICON_ZOOM - 1, last.startZoom + 4);
    if (boundary <= last.startZoom) return;
    writeBands([
      ...bands.slice(0, -1),
      { ...last, endZoom: boundary },
      { startZoom: boundary, endZoom: MAX_ICON_ZOOM, size: Number((last.size + 0.1).toFixed(2)) }
    ]);
  };
  const removeBand = (index: number) => writeBands(bands.filter((_, i) => i !== index));

  const setChoice = (index: number, patch: Partial<CmStyleChoice>) =>
    writeVectorStyle(
      choices.map((c, i) => (i === index ? { ...c, ...patch } : c)),
      fallback,
      fieldKey,
      iconSize
    );
  const addChoice = () => writeVectorStyle([...choices, emptyChoice(geometryType, primaryColor)], fallback, fieldKey, iconSize);
  const removeChoice = (index: number) =>
    writeVectorStyle(
      choices.filter((_, i) => i !== index),
      fallback,
      fieldKey,
      iconSize
    );
  /**
   * The appearance a newly prefilled value gets: the next palette colour, or the
   * dataset's default icon for point geometries (an icon file cannot be invented,
   * and a blank one renders as nothing, which reads as data loss).
   */
  const styleForNewValue = useCallback(
    (_value: string, index: number): Partial<CmStyleChoice> =>
      iconStyled ? { icon: fallback.icon ?? "" } : { color: categoryColor(index) },
    [iconStyled, fallback.icon]
  );

  /**
   * Whether the values for `field` may be filled in WITHOUT anyone asking.
   *
   * Two guards, both about not authoring a huge style as a side effect of opening a
   * dialog or picking a field:
   *
   *  - **An identifier column is never auto-filled.** `high_cardinality` means the
   *    column reads as a key, so styling by it is ~one rule per feature — for
   *    `osm_id` on 5,000 features that is a 5,000-rule style nobody requested. The
   *    worker only flags columns with ≥50 distinct values over ≥50 features, so a
   *    genuine category column loses nothing but the convenience.
   *  - **A complete list is capped.** `values` can legitimately hold up to
   *    {@link ENUMERATE_MAX}; prefilling 500 rules unasked is the same problem in
   *    milder form.
   *
   * Neither guard blocks the operator: the explicit "Style N remaining values"
   * action fills whatever is listed, and a single value can always be picked by
   * hand with "Add category".
   */
  const mayAutoFill = useCallback((discovered: ReturnType<typeof spatialFieldValues>) => {
    if (discovered.highCardinality) return false;
    return discovered.values.length > 0 && discovered.values.length <= AUTO_FILL_MAX;
  }, []);

  /** Merges values in: nothing already styled is restyled, nothing is duplicated. */
  const fillCategories = useCallback(
    (values: string[]) => {
      const merged = mergeCategoryChoices(choices, values, styleForNewValue);
      if (!merged.added.length && merged.choices.length === choices.length) return;
      writeVectorStyle(merged.choices, fallback, fieldKey, iconSize);
    },
    [choices, fallback, fieldKey, iconSize, styleForNewValue, writeVectorStyle]
  );

  // Choosing a field prefills its values straight away. Unlike CM tables the values
  // arrive with the dataset record, so there is nothing to wait for.

  /**
   * A dataset opened with a field already selected but **no categories** is
   * prefilled immediately.
   *
   * The field key defaults to the style's own, or failing that the first attribute
   * column, so it is usually already populated when the dialog opens — which means
   * "select the field" is a no-op and nothing would ever be prefilled without this.
   * Restricted to the empty case: once even one category exists, prefilling becomes
   * the explicit action below, so opening a style someone has worked on never
   * changes it behind their back.
   */
  const didInitialFill = useRef(false);
  useEffect(() => {
    // Never for someone who cannot style: there is no save to carry the fill.
    if (readOnly || didInitialFill.current || isRaster || geometry === "none") return;
    didInitialFill.current = true;
    if (choices.length || !fieldKey) return;
    const discovered = spatialFieldValues(dataset.attribute_schema, fieldKey);
    if (!mayAutoFill(discovered)) return;
    fillCategories(discovered.values);
  }, [readOnly, isRaster, geometry, choices.length, fieldKey, dataset.attribute_schema, fillCategories, mayAutoFill]);

  /**
   * Switching the styling field REPLACES the categories — it never adds to them.
   *
   * A category is a value of one particular column: the rule it becomes filters on
   * `fieldKey`, so carrying "Kimberley" over from `region` to `status` writes a
   * rule matching features whose status is Kimberley, which is nothing. Keeping
   * them also read as the new field's values being appended to the old field's.
   * So the categories are rebuilt from the field just chosen, prefilled when that
   * is allowed and left empty when it is not (an identifier column, or more
   * values than {@link AUTO_FILL_MAX}) — where the explicit "Style N remaining
   * values" action takes over.
   */
  const changeFieldKey = (next: string) => {
    const key = next.trim();
    if (key === fieldKey) return;

    const discovered = key ? spatialFieldValues(dataset.attribute_schema, key) : undefined;
    const prefilled = discovered && mayAutoFill(discovered) ? mergeCategoryChoices([], discovered.values, styleForNewValue).choices : [];
    writeVectorStyle(prefilled, fallback, key, iconSize);
  };

  /**
   * Reassigns a palette colour to every category — the only action that overwrites
   * colours already chosen, so it is never automatic.
   */
  const shuffleColors = () => {
    const offset = Math.floor(Math.random() * 10) + 1;
    writeVectorStyle(
      choices.map((choice, i) => ({ ...choice, color: categoryColor(i + offset) })),
      fallback,
      fieldKey,
      iconSize
    );
  };

  // ── Raster state (meaningful only when isRaster) ──────────────────────────
  const [rasterValues, setRasterValues] = useState<SpatialRasterTier1Values>(() => getRasterTier1Values(displayStyle));
  const writeRasterValue = (patch: Partial<SpatialRasterTier1Values>) => {
    const next = { ...rasterValues, ...patch };
    setRasterValues(next);
    setDisplayStyle((prev) => buildUpdatedRasterStyle(prev, next));
  };

  /** Direct JSON edit — re-seed the controls so the two views never disagree. */
  const onJsonChange = (parsed: Record<string, any>) => {
    setDisplayStyle(parsed);
    if (isRaster) {
      setRasterValues(getRasterTier1Values(parsed));
    } else {
      setChoices(getSpatialVectorStyleChoices(parsed, geometryType));
      setFallback(defaultChoiceFrom(parsed, geometryType, primaryColor));
      setFieldKey(getSpatialVectorStyleFieldKey(parsed) ?? fieldKey);
      setIconSize(iconStyled ? getIconSize(parsed) : undefined);
    }
  };

  const submit = () => onSave(displayStyle);

  const outputControl = (choice: CmStyleChoice, onPatch: (patch: Partial<CmStyleChoice>) => void, label: string) =>
    iconStyled ? (
      <IconField label={label} value={choice.icon} onChange={(icon) => onPatch({ icon })} orgId={orgId} disabled={readOnly} />
    ) : (
      <Stack direction='row' spacing='20px'>
        <ColorField label={label} value={choice.color} onChange={(color) => onPatch({ color })} disabled={readOnly} />
        {geometry === "polygon" && (
          <ColorField
            label='Outline'
            value={choice.outlineColor}
            onChange={(outlineColor) => onPatch({ outlineColor })}
            disabled={readOnly}
          />
        )}
        {geometry === "linestring" && (
          <TextField
            size='small'
            type='number'
            label='Width'
            value={choice.width ?? ""}
            disabled={readOnly}
            onChange={(e) => onPatch({ width: e.target.value === "" ? undefined : Number(e.target.value) })}
            sx={{ width: 110 }}
          />
        )}
      </Stack>
    );

  return (
    <>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Style
        <Typography variant='body2' sx={{ color: "text.secondary" }}>
          {dataset.name}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing='20px'>
          {readOnly && <Alert severity='info'>{STYLING_MANAGED_MESSAGE}</Alert>}

          {isRaster ? (
            <>
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: "4px" }}>
                  Appearance
                </Typography>
                <Typography variant='body2' sx={{ color: "text.secondary" }}>
                  Applies to the rendered tile as-is — works the same whether this dataset displays as a COG or as raster tiles.
                </Typography>
              </Box>

              <Box sx={{ maxWidth: 420 }}>
                <Typography variant='caption' sx={{ color: "text.secondary" }}>
                  Opacity
                </Typography>
                <Slider
                  value={rasterValues.opacity ?? 1}
                  onChange={(_, v) => writeRasterValue({ opacity: v as number })}
                  disabled={readOnly}
                  min={0}
                  max={1}
                  step={0.05}
                  marks={OPACITY_MARKS}
                  valueLabelDisplay='auto'
                />
              </Box>

              <Stack direction='row' spacing='20px'>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='caption' sx={{ color: "text.secondary" }}>
                    Brightness min
                  </Typography>
                  <Slider
                    value={rasterValues.brightnessMin ?? 0}
                    onChange={(_, v) => writeRasterValue({ brightnessMin: v as number })}
                    disabled={readOnly}
                    min={0}
                    max={1}
                    step={0.05}
                    valueLabelDisplay='auto'
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='caption' sx={{ color: "text.secondary" }}>
                    Brightness max
                  </Typography>
                  <Slider
                    value={rasterValues.brightnessMax ?? 1}
                    onChange={(_, v) => writeRasterValue({ brightnessMax: v as number })}
                    disabled={readOnly}
                    min={0}
                    max={1}
                    step={0.05}
                    valueLabelDisplay='auto'
                  />
                </Box>
              </Stack>

              <Stack direction='row' spacing='20px'>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='caption' sx={{ color: "text.secondary" }}>
                    Saturation
                  </Typography>
                  <Slider
                    value={rasterValues.saturation ?? 0}
                    onChange={(_, v) => writeRasterValue({ saturation: v as number })}
                    disabled={readOnly}
                    min={-1}
                    max={1}
                    step={0.05}
                    valueLabelDisplay='auto'
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='caption' sx={{ color: "text.secondary" }}>
                    Contrast
                  </Typography>
                  <Slider
                    value={rasterValues.contrast ?? 0}
                    onChange={(_, v) => writeRasterValue({ contrast: v as number })}
                    disabled={readOnly}
                    min={-1}
                    max={1}
                    step={0.05}
                    valueLabelDisplay='auto'
                  />
                </Box>
              </Stack>

              <Stack direction='row' spacing='20px' sx={{ alignItems: "center" }}>
                <TextField
                  size='small'
                  type='number'
                  label='Hue rotate (°)'
                  value={rasterValues.hueRotate ?? 0}
                  onChange={(e) => writeRasterValue({ hueRotate: Number(e.target.value) })}
                  disabled={readOnly}
                  inputProps={{ min: 0, max: 360, step: 1 }}
                  helperText='Web map only — QGIS has no hue rotation'
                  sx={{ width: 160 }}
                />
                <TextField
                  select
                  size='small'
                  label='Resampling'
                  value={rasterValues.resampling ?? "linear"}
                  onChange={(e) => writeRasterValue({ resampling: e.target.value as "linear" | "nearest" })}
                  disabled={readOnly}
                  sx={{ width: 160 }}
                >
                  <MenuItem value='linear'>Linear (smooth)</MenuItem>
                  <MenuItem value='nearest'>Nearest (pixelated)</MenuItem>
                </TextField>
                <TextField
                  size='small'
                  type='number'
                  label='Fade duration (ms)'
                  value={rasterValues.fadeDuration ?? 300}
                  onChange={(e) => writeRasterValue({ fadeDuration: Number(e.target.value) })}
                  disabled={readOnly}
                  inputProps={{ min: 0, step: 50 }}
                  sx={{ width: 170 }}
                />
              </Stack>

              <Divider />

              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: "4px" }}>
                  Colour map
                </Typography>
                <Alert severity='info'>
                  Recolouring by pixel value isn't available yet. Rasters are currently converted with a fixed brightness stretch, so there
                  are no real values here for a colour ramp to map — only the appearance controls above apply. This needs a backend/worker
                  change; ask your platform admin about its status.
                </Alert>
              </Box>
            </>
          ) : geometry === "none" ? (
            <Alert severity='info'>
              This dataset's geometry hasn't been classified yet, so there's nothing to style. Try again once processing finishes.
            </Alert>
          ) : (
            <>
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: "4px" }}>
                  Styling
                </Typography>
                <Typography variant='body2' sx={{ color: "text.secondary" }}>
                  {iconStyled
                    ? "Each category draws its own icon. Features without a matching category use the default."
                    : "Each category draws its own colour. Features without a matching category use the default."}
                </Typography>
              </Box>

              <Stack direction='row' spacing='20px' sx={{ alignItems: "flex-start" }}>
                <Autocomplete
                  // Not `freeSolo`: a column the dataset does not have styles nothing,
                  // and committing every keystroke would rebuild the categories on the
                  // way to the name. Typing still narrows the list.
                  options={fieldOptions}
                  disabled={readOnly}
                  value={fieldKey || null}
                  onChange={(_, value) => changeFieldKey(value ?? "")}
                  sx={{ width: 320, flexShrink: 0 }}
                  // Identifier columns stay selectable — styling one known id is a
                  // legitimate use — but are labelled so they do not sit in the list
                  // looking as styleable as a real category column.
                  renderOption={(props, option) => (
                    <Box component='li' {...props} sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                      <span>{option}</span>
                      {identifierFields.has(option) && (
                        <Typography variant='caption' sx={{ color: "text.secondary", flexShrink: 0 }}>
                          identifier
                        </Typography>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => <TextField {...params} size='small' label='Style by field' helperText={fieldHelperText} />}
                />
                {fieldKey && !readOnly && (
                  <Stack spacing='4px' sx={{ pt: "4px" }}>
                    <Typography variant='body2' sx={{ color: "text.secondary" }}>
                      {fieldValues.values.length === 0
                        ? `No values recorded for "${fieldKey}".`
                        : `${fieldValues.values.length} value${fieldValues.values.length === 1 ? "" : "s"} · ${
                            styledValues.size
                          } styled${unstyledCount ? ` · ${unstyledCount} not styled yet` : ""}`}
                    </Typography>
                    {unstyledCount > 0 && (
                      <Button
                        size='small'
                        startIcon={<AutoAwesomeOutlinedIcon />}
                        onClick={() => fillCategories(fieldValues.values)}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {`Style ${unstyledCount} remaining value${unstyledCount === 1 ? "" : "s"}`}
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>

              {/* An identifier column is never prefilled automatically — styling by it
                  would author roughly one rule per feature — so say why, and leave the
                  explicit action available for the rare legitimate case. */}
              {fieldKey && !readOnly && fieldValues.highCardinality && (
                <Alert severity='warning'>
                  Nearly every feature has its own value in <strong>{fieldKey}</strong>, so this reads as an identifier rather than a
                  category. Styling by it would create about one rule per feature, so its values are not filled in automatically.
                  {!fieldValues.complete && ` More than ${ENUMERATE_MAX} distinct values — too many to list.`} You can still pick a single
                  value with <strong>Add category</strong> to style just that one.
                </Alert>
              )}

              {/* `values` absent means UNKNOWN, not "a few" — so this shows for any
                  un-reprocessed dataset, including columns that happen to be complete.
                  Suppressed for an identifier column, which says its own thing above. */}
              {fieldKey && !readOnly && !fieldValues.unknownField && !fieldValues.complete && !fieldValues.highCardinality && (
                <Alert severity='info'>
                  Only a sample of this column's values is listed{fieldValues.values.length ? ` (${fieldValues.values.length})` : ""} — this
                  dataset was processed before full value lists were available. Re-process it to style a value that is missing from the
                  list.
                </Alert>
              )}

              {fieldKey && !readOnly && !fieldValues.unknownField && fieldValues.source === "none" && (
                <Alert severity='info'>
                  This dataset records no values for "{fieldKey}", so there is nothing to style by. Pick a different field, or re-process
                  the dataset to record this column's values.
                </Alert>
              )}

              <Stack spacing='20px'>
                {choices.map((choice, i) => (
                  <Stack key={i} direction='row' spacing='20px' sx={{ alignItems: "flex-start" }}>
                    <Autocomplete
                      // Not `freeSolo`: a category only means something if the column
                      // actually holds it, and a typed one is how the same value ends
                      // up styled twice. Typing still narrows the list.
                      options={optionsForChoice(choice)}
                      disabled={readOnly}
                      value={choice.value || null}
                      onChange={(_, value) => setChoice(i, { value: value ?? "" })}
                      sx={{ width: 220, flexShrink: 0 }}
                      renderInput={(params) => <TextField {...params} size='small' label='Category value' />}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>{outputControl(choice, (patch) => setChoice(i, patch), "Style")}</Box>
                    {!readOnly && (
                      <Tooltip title='Remove category'>
                        <IconButton aria-label={`Remove ${choice.value || "category"}`} onClick={() => removeChoice(i)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ))}

                {choices.length === 0 && (
                  <Typography variant='body2' sx={{ color: "text.secondary", fontStyle: "italic" }}>
                    No categories yet — every feature uses the default below.
                  </Typography>
                )}

                {!readOnly && (
                  <Stack direction='row' spacing='8px' sx={{ alignSelf: "flex-start" }}>
                    <Button startIcon={<AddOutlinedIcon />} onClick={addChoice}>
                      Add category
                    </Button>
                    {!iconStyled && choices.length > 1 && (
                      // `describeChild` so the button keeps its own accessible name — a MUI
                      // Tooltip otherwise replaces the name with the tooltip text.
                      <Tooltip describeChild title='Reassign a new colour to every category — this replaces colours you have already set'>
                        <Button startIcon={<ShuffleOutlinedIcon />} onClick={shuffleColors}>
                          Shuffle colours
                        </Button>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Stack>

              {iconStyled && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: "4px" }}>
                      Icon size
                    </Typography>
                    <Typography variant='body2' sx={{ color: "text.secondary", mb: "12px" }}>
                      Size is a multiplier on the image, so a large PNG needs a small number. One row means one size at every zoom; add rows
                      to draw icons smaller when zoomed out.
                    </Typography>

                    {iconSize === undefined ? (
                      <Alert severity='info'>
                        This dataset's icon size uses an expression the form can't edit. Change it in the GeoStyler JSON below, or replace
                        it with simple zoom ranges here.
                        {!readOnly && (
                          <Box sx={{ mt: "12px" }}>
                            <Button size='small' variant='outlined' onClick={() => writeBands(iconSizeToBands(DEFAULT_ICON_SIZE))}>
                              Use zoom ranges
                            </Button>
                          </Box>
                        )}
                      </Alert>
                    ) : (
                      <Stack spacing='20px'>
                        {bands.map((band, i) => (
                          <Stack key={i} direction='row' spacing='20px' sx={{ alignItems: "center" }}>
                            <TextField
                              size='small'
                              type='number'
                              label='Start zoom'
                              value={band.startZoom}
                              disabled
                              helperText={i === 0 ? "Always 0" : "Set by the row above"}
                              sx={{ width: 150 }}
                            />
                            <TextField
                              size='small'
                              type='number'
                              label='Icon size'
                              value={band.size}
                              disabled={readOnly}
                              onChange={(e) => setBandSize(i, Number(e.target.value))}
                              inputProps={{ step: 0.05, min: 0 }}
                              sx={{ width: 130 }}
                            />
                            <TextField
                              size='small'
                              type='number'
                              label='End zoom'
                              value={band.endZoom}
                              disabled={readOnly || i === bands.length - 1}
                              onChange={(e) => setBandEnd(i, Number(e.target.value))}
                              inputProps={{ step: 1, min: 0, max: MAX_ICON_ZOOM }}
                              helperText={i === bands.length - 1 ? `Always ${MAX_ICON_ZOOM}` : undefined}
                              sx={{ width: 150 }}
                            />
                            {!readOnly && (
                              <Tooltip title={bands.length === 1 ? "At least one range is required" : "Remove this range"}>
                                <span>
                                  <IconButton
                                    aria-label={`Remove zoom range ${i + 1}`}
                                    onClick={() => removeBand(i)}
                                    disabled={bands.length === 1}
                                  >
                                    <DeleteOutlineIcon fontSize='small' />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        ))}
                        {!readOnly && (
                          <Button
                            startIcon={<AddOutlinedIcon />}
                            onClick={addBand}
                            disabled={bands[bands.length - 1].startZoom >= MAX_ICON_ZOOM - 1}
                            sx={{ alignSelf: "flex-start" }}
                          >
                            Add zoom range
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Box>
                </>
              )}

              <Divider />

              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: "12px" }}>
                  Default
                </Typography>
                <Box sx={{ maxWidth: 520 }}>
                  {outputControl(
                    fallback,
                    (patch) => writeVectorStyle(choices, { ...fallback, ...patch }, fieldKey, iconSize),
                    "Default style"
                  )}
                </Box>
              </Box>
            </>
          )}

          <Accordion elevation={0} disableGutters sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                Advanced — GeoStyler JSON
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <JsonField
                label='Display style'
                value={displayStyle}
                onChange={onJsonChange}
                helperText='The full stored style. Anything the form above does not cover lives here.'
                minRows={10}
                readOnly={readOnly}
              />
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: "24px", py: "16px" }}>
        {readOnly ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant='contained' onClick={submit} disabled={jsonInvalid || saving}>
              {saving ? "Saving…" : jsonInvalid ? "Fix JSON to save" : "Save style"}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
};

export default SpatialDatasetStyleDialog;
